/*
 * SPDX-FileCopyrightText: 2021 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { ScrollSource } from '@components/editor-page/editor-page-content'
import { store } from '..'
import { Logger } from '../../utils/logger'
import type {
  EditorConfig,
  SetEditorDocChangeAction,
  SetEditorLigaturesAction,
  SetEditorLineWrappingAction,
  SetEditorSmartPasteAction,
  SetEditorSyncScrollAction,
  SetScrollSourceAction
} from './types'
import { EditorConfigActionType } from './types'

const log = new Logger('Redux > Editor')

export const loadFromLocalStorage = (): EditorConfig | undefined => {
  try {
    const stored = window.localStorage.getItem('editorConfig')
    if (!stored) {
      return undefined
    }
    return JSON.parse(stored) as EditorConfig
  } catch (_) {
    return undefined
  }
}

export const saveToLocalStorage = (editorConfig: EditorConfig): void => {
  try {
    const json = JSON.stringify(editorConfig)
    localStorage.setItem('editorConfig', json)
  } catch (error) {
    log.error('Error while saving editor config in local storage', error)
  }
}

export const setEditorSyncScroll = (syncScroll: boolean): void => {
  const action: SetEditorSyncScrollAction = {
    type: EditorConfigActionType.SET_SYNC_SCROLL,
    syncScroll
  }
  store.dispatch(action)
}

export const setEditorLineWrapping = (lineWrapping: boolean): void => {
  const action: SetEditorLineWrappingAction = {
    type: EditorConfigActionType.SET_LINE_WRAPPING,
    lineWrapping
  }
  store.dispatch(action)
}

export const setEditorLigatures = (ligatures: boolean): void => {
  const action: SetEditorLigaturesAction = {
    type: EditorConfigActionType.SET_LIGATURES,
    ligatures
  }
  store.dispatch(action)
}

export const setEditorSmartPaste = (smartPaste: boolean): void => {
  const action: SetEditorSmartPasteAction = {
    type: EditorConfigActionType.SET_SMART_PASTE,
    smartPaste
  }
  store.dispatch(action)
}

export const setScrollSource = (scrollSource: ScrollSource): void => {
  const action: SetScrollSourceAction = {
    type: EditorConfigActionType.SET_SCROLL_SOURCE,
    scrollSource
  }
  store.dispatch(action)
}

export const setEditorDocChange = (hasEditorDocChange: boolean): void => {
  const action: SetEditorDocChangeAction = {
    type: EditorConfigActionType.SET_EDITOR_DOC_CHANGE,
    editorDocChange: hasEditorDocChange,
  }
  store.dispatch(action)
}
